import { Type, Static } from '@sinclair/typebox'
import omit from 'lodash.omit'
import { v4 as uuidv4 } from 'uuid'

import { HerbicidePesticideUsedTB, NumberMinZeroDefaultUndefinedTB, type SeasonalValuesDefaultT, SeasonTB } from './report_shared'

import { CATTLE_CLASSES, SHEEP_CLASSES } from '@ruminati/constants/gaf/sb'
import { CROP_TYPES, CROP_PRODUCTION_SYSTEMS } from '@ruminati/constants/gaf/grains'
import { FERTILISER_OPTIONS } from '@ruminati/constants/chemical'
import { PropertyT } from './property'
import { ReportWithPartialInputsT } from './report'
import { OptionalNullableCleanString } from './utils/shared-types'

export type SeasonalFigures = {
  spring: number
  summer: number
  autumn: number
  winter: number
}
export type SeasonKey = "spring" | "summer" | "autumn" | "winter"
export type PartialSeasonalFigures = Partial<SeasonalFigures>

export const CattleTB = Type.Array(Type.Object({
  name: OptionalNullableCleanString,
  classId: Type.Union(
    CATTLE_CLASSES.map(c => Type.Literal(c.id))
  ),
  headcount: SeasonTB({}),
  liveweight: SeasonTB({ maximum: 1500 }),
  liveweightGain: SeasonTB({ maximum: 4, minimum: -4 })
}), { minItems: 1, default: [] })
export type CattleT = Static<typeof CattleTB>

export const SheepTB = Type.Array(Type.Object({
  name: OptionalNullableCleanString,
  classId: Type.Union(
    SHEEP_CLASSES.map(c => Type.Literal(c.id))
  ),
  headcount: SeasonTB({}),
  liveweight: SeasonTB({ maximum: 300 }),
  liveweightGain: SeasonTB({ maximum: 4, minimum: -4 })
}), { minItems: 1, default: [] })
export type SheepT = Static<typeof SheepTB>

export const CalvingRecordTB = Type.Object({
  percentageCalving: SeasonTB({ maximum: 1 })
})
export type CalvingRecordT = Static<typeof CalvingRecordTB>

export const CalvingFormTB = Type.Object({
  percentageCalving: SeasonTB({ maximum: 100 })
})
export type CalvingFormT = Static<typeof CalvingFormTB>

// transform functions from db -> client form and client form -> db
export function transformCalvingRecordToForm(record: CalvingRecordT): CalvingFormT {
  return {
    ...record,
    percentageCalving: Object.entries(record.percentageCalving).reduce((acc, [key, value]) => {
      // @ts-expect-error: typing of reduce
      acc[key] = value * 100;
      return acc;
    }, {}) as SeasonalValuesDefaultT // decimal -> percentage
  }
}
export function transformCalvingFormToRecord(form: CalvingFormT): CalvingRecordT {
  return {
    ...form,
    percentageCalving: Object.entries(form.percentageCalving).reduce((acc, [key, value]) => {
      // @ts-expect-error: typing of reduce
      acc[key] = value / 100;
      return acc;
    }, {}) as SeasonalValuesDefaultT // percentage -> decimal
  }
}

export const LambingAndWoolRecordTB = Type.Object({
  proportionEwesLambing: SeasonTB({ maximum: 1 }),
  seasonalLambingRates: SeasonTB(),
  greasyWoolKgs: NumberMinZeroDefaultUndefinedTB(),
  cleanWoolYieldPercentage: NumberMinZeroDefaultUndefinedTB(),
  numberSheepShorn: NumberMinZeroDefaultUndefinedTB()
})
export type LambingAndWoolRecordT = Static<typeof LambingAndWoolRecordTB>

export const LambingAndWoolFormTB = Type.Object({
  proportionEwesLambing: SeasonTB({ maximum: 100 }),
  seasonalLambingRates: SeasonTB(),
  greasyWoolKgs: NumberMinZeroDefaultUndefinedTB(),
  cleanWoolYieldPercentage: NumberMinZeroDefaultUndefinedTB(),
  numberSheepShorn: NumberMinZeroDefaultUndefinedTB()
})
export type LambingAndWoolFormT = Static<typeof LambingAndWoolFormTB>

// transform functions from db -> client form and client form -> db
export function transformLambingAndWoolRecordToForm(record: LambingAndWoolRecordT): LambingAndWoolFormT {
  return {
    ...record,
    proportionEwesLambing: Object.entries(record.proportionEwesLambing).reduce((acc, [key, value]) => {
      // @ts-expect-error: typing of reduce
      acc[key] = value * 100;
      return acc;
    }, {}) as SeasonalValuesDefaultT, // decimal -> percentage
    seasonalLambingRates: Object.entries(record.seasonalLambingRates).reduce((acc, [key, value]) => {
      // @ts-expect-error: typing of reduce
      acc[key] = value * 100;
      return acc;
    }, {}) as SeasonalValuesDefaultT, // decimal -> percentage
    cleanWoolYieldPercentage: record.cleanWoolYieldPercentage * 100 // decimal -> percentage
  }
}
export function transformLambingAndWoolFormToRecord(form: LambingAndWoolFormT): LambingAndWoolRecordT {
  return {
    ...form,
    proportionEwesLambing: Object.entries(form.proportionEwesLambing).reduce((acc, [key, value]) => {
      // @ts-expect-error: typing of reduce
      acc[key] = value / 100;
      return acc;
    }, {}) as SeasonalValuesDefaultT, // percentage -> decimal
    seasonalLambingRates: Object.entries(form.seasonalLambingRates).reduce((acc, [key, value]) => {
      // @ts-expect-error: typing of reduce
      acc[key] = value / 100;
      return acc;
    }, {}) as SeasonalValuesDefaultT, // percentage -> decimal
    cleanWoolYieldPercentage: form.cleanWoolYieldPercentage / 100 // percentage -> decimal
  }
}

export const DSE_UnitTB = Type.Literal("DSE")
export const AE_UnitTB = Type.Literal("AE")
export const LivestockUnitTB = Type.Union([DSE_UnitTB, AE_UnitTB])
export type LivestockUnitT = Static<typeof LivestockUnitTB>

export const LivestockRecordTB = Type.Object({
  beefDSE: Type.Optional(NumberMinZeroDefaultUndefinedTB()),
  beefUnits: LivestockUnitTB,
  sheepDSE: Type.Optional(NumberMinZeroDefaultUndefinedTB()),
  sheepUnits: LivestockUnitTB,
})
export type LivestockRecordT = Static<typeof LivestockRecordTB>

export const LivestockFormTB = Type.Object({
  beefDSE: Type.Optional(NumberMinZeroDefaultUndefinedTB()),
  units: LivestockUnitTB,
  sheepDSE: Type.Optional(NumberMinZeroDefaultUndefinedTB()),
})
export type LivestockFormT = Static<typeof LivestockFormTB>

// transform functions from db -> client form and client form -> db
export function transformLivestockRecordToForm(record: LivestockRecordT): LivestockFormT {
  return {
    beefDSE: record.beefDSE,
    sheepDSE: record.sheepDSE,
    units: record.beefUnits
  }
}
export function transformLivestockFormToRecord(form: LivestockFormT): LivestockRecordT {
  return {
    beefDSE: form.beefDSE,
    sheepDSE: form.sheepDSE,
    beefUnits: form.units,
    sheepUnits: form.units
  }
}

export const ProducerGrainsPreV3_4RecordTB = Type.Array(Type.Object({
  stableId: Type.String(),
  cropType: Type.Union(CROP_TYPES.map(c => Type.Literal(c.value))),
  name: OptionalNullableCleanString,
  productionSystem: Type.Union(CROP_PRODUCTION_SYSTEMS.map(c => Type.Literal(c.value))),
  areaSown: NumberMinZeroDefaultUndefinedTB(),
  averageYield: NumberMinZeroDefaultUndefinedTB(),
  mapApplied: NumberMinZeroDefaultUndefinedTB(),
  dapApplied: NumberMinZeroDefaultUndefinedTB(),
  soaApplied: NumberMinZeroDefaultUndefinedTB(),
  ureaApplied: NumberMinZeroDefaultUndefinedTB(),
  singleSuperphosphateApplied: NumberMinZeroDefaultUndefinedTB(),
  herbicidePesticideApplied: NumberMinZeroDefaultUndefinedTB(),
  glyphosateApplied: NumberMinZeroDefaultUndefinedTB(),
  limeApplied: NumberMinZeroDefaultUndefinedTB(),
  fractionAnnualBurntCrop: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
}))
export type ProducerGrainsPreV3_4RecordT = Static<typeof ProducerGrainsPreV3_4RecordTB>

export const ProducerGrainsPreV3_4FormTB = Type.Array(Type.Object({
  stableId: Type.String(),
  cropType: Type.Union(CROP_TYPES.map(c => Type.Literal(c.value))),
  name: OptionalNullableCleanString,
  productionSystem: Type.Union(CROP_PRODUCTION_SYSTEMS.map(c => Type.Literal(c.value))),
  areaSown: NumberMinZeroDefaultUndefinedTB(),
  averageYield: NumberMinZeroDefaultUndefinedTB(),
  mapApplied: NumberMinZeroDefaultUndefinedTB(),
  dapApplied: NumberMinZeroDefaultUndefinedTB(),
  soaApplied: NumberMinZeroDefaultUndefinedTB(),
  ureaApplied: NumberMinZeroDefaultUndefinedTB(),
  singleSuperphosphateApplied: NumberMinZeroDefaultUndefinedTB(),
  herbicidePesticideApplied: NumberMinZeroDefaultUndefinedTB(),
  glyphosateApplied: NumberMinZeroDefaultUndefinedTB(),
  limeApplied: NumberMinZeroDefaultUndefinedTB(),
  fractionAnnualBurntCrop: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
}))
export type ProducerGrainsPreV3_4FormT = Static<typeof ProducerGrainsPreV3_4FormTB>

// transform functions from db -> client form and client form -> db
export function transformProducerGrainsPreV3_4RecordToForm(record: ProducerGrainsPreV3_4RecordT): ProducerGrainsPreV3_4FormT {
  return record.map(r => ({
    ...r,
    fractionAnnualBurntCrop: r.fractionAnnualBurntCrop * 100, // decimal -> percentage
  }))
}
export function transformProducerGrainsPreV3_4FormToRecord(form: ProducerGrainsPreV3_4FormT): ProducerGrainsPreV3_4RecordT {
  return form.map(f => ({
    ...f,
    stableId: f.stableId ?? uuidv4(), // generate a stableId if a new grain record
    fractionAnnualBurntCrop: f.fractionAnnualBurntCrop / 100, // percentage - >decimal
  }))
}

export const ProducerGrainsActiveIngredientTypeNonUreaNitrogenTB = Type.Literal('nonUreaNitrogen')
export const ProducerGrainsActiveIngredientTypePhosphorusTB = Type.Literal('phosphorus')
export const ProducerGrainsActiveIngredientTypePotassiumTB = Type.Literal('potassium')
export const ProducerGrainsActiveIngredientTypeSulfurTB = Type.Literal('sulfur')

export const ProducerGrainsActiveIngredientTypeTB = Type.Union([
  ProducerGrainsActiveIngredientTypeNonUreaNitrogenTB,
  ProducerGrainsActiveIngredientTypePhosphorusTB,
  ProducerGrainsActiveIngredientTypePotassiumTB,
  ProducerGrainsActiveIngredientTypeSulfurTB
])
export type ProducerGrainsActiveIngredientTypeT = Static<typeof ProducerGrainsActiveIngredientTypeTB>

export const ProducerGrainsActiveIngredientTypeMapTB = Type.Object({
  'Non-Urea Nitrogen': ProducerGrainsActiveIngredientTypeNonUreaNitrogenTB,
  'Phosphorus': ProducerGrainsActiveIngredientTypePhosphorusTB,
  'Potassium': ProducerGrainsActiveIngredientTypePotassiumTB,
  'Sulfur': ProducerGrainsActiveIngredientTypeSulfurTB,
})
export type ProducerGrainsActiveIngredientTypeMapT = Static<typeof ProducerGrainsActiveIngredientTypeMapTB>

export const ProducerFertiliserTB = Type.Object({
  id: Type.Union(FERTILISER_OPTIONS.map(f => Type.Literal(f.id)), {
    default: undefined
  }),
  rateApplied: NumberMinZeroDefaultUndefinedTB()
})
export type ProducerFertiliserT = Static<typeof ProducerFertiliserTB>

export const ProducerGrainsPostV3_3RecordTB = Type.Array(Type.Object({
  stableId: Type.String(),
  cropType: Type.Union(CROP_TYPES.map(c => Type.Literal(c.value))),
  name: OptionalNullableCleanString,
  productionSystem: Type.Union(CROP_PRODUCTION_SYSTEMS.map(c => Type.Literal(c.value))),
  areaSown: NumberMinZeroDefaultUndefinedTB(),
  averageYield: NumberMinZeroDefaultUndefinedTB(),
  urea: NumberMinZeroDefaultUndefinedTB(),
  uan: NumberMinZeroDefaultUndefinedTB(),
  fractionAnnualBurntCrop: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
  lime: NumberMinZeroDefaultUndefinedTB(),
  limeFraction: NumberMinZeroDefaultUndefinedTB({ maximum: 1 }),
  herbicidesPesticidesUsed: Type.Optional(Type.Array(HerbicidePesticideUsedTB)),
  herbicide: NumberMinZeroDefaultUndefinedTB(),
  herbicidePesticideApplied: NumberMinZeroDefaultUndefinedTB(),
  fertilisers: Type.Optional(Type.Array(ProducerFertiliserTB)),
  nonUreaNitrogen: NumberMinZeroDefaultUndefinedTB(),
  phosphorus: NumberMinZeroDefaultUndefinedTB(),
  potassium: NumberMinZeroDefaultUndefinedTB(),
  sulfur: NumberMinZeroDefaultUndefinedTB()
}))
export type ProducerGrainsPostV3_3RecordT = Static<typeof ProducerGrainsPostV3_3RecordTB>

export const ProducerGrainPostV3_3FormTB = Type.Object({
  stableId: Type.Optional(Type.String()), // stable id's generated in transformer if not present, see below
  cropType: Type.Union(CROP_TYPES.map(c => Type.Literal(c.value))),
  name: OptionalNullableCleanString,
  productionSystem: Type.Union(CROP_PRODUCTION_SYSTEMS.map(c => Type.Literal(c.value))),
  areaSown: NumberMinZeroDefaultUndefinedTB(),
  averageYield: NumberMinZeroDefaultUndefinedTB(),
  urea: NumberMinZeroDefaultUndefinedTB(),
  uan: NumberMinZeroDefaultUndefinedTB(),
  fractionAnnualBurntCrop: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  lime: NumberMinZeroDefaultUndefinedTB(),
  limeFraction: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  herbicidesPesticidesUsed: Type.Optional(Type.Array(HerbicidePesticideUsedTB)),
  herbicide: NumberMinZeroDefaultUndefinedTB(),
  herbicidePesticideApplied: NumberMinZeroDefaultUndefinedTB(),
  fertilisers: Type.Optional(Type.Array(ProducerFertiliserTB)),
  activeIngredients: Type.Object({
    nonUreaNitrogen: NumberMinZeroDefaultUndefinedTB(),
    phosphorus: NumberMinZeroDefaultUndefinedTB(),
    potassium: NumberMinZeroDefaultUndefinedTB(),
    sulfur: NumberMinZeroDefaultUndefinedTB(),
  })
})
export type ProducerGrainPostV3_3FormT = Static<typeof ProducerGrainPostV3_3FormTB>

export const ProducerGrainsPostV3_3FormTB = Type.Array(ProducerGrainPostV3_3FormTB)
export type ProducerGrainsPostV3_3FormT = Static<typeof ProducerGrainsPostV3_3FormTB>

// transform functions from db -> client form and client form -> db
export function transformProducerGrainsPostV3_3RecordToForm(record: ProducerGrainsPostV3_3RecordT): ProducerGrainsPostV3_3FormT {
  return record.map(r => ({
    ...r,
    activeIngredients: {
      nonUreaNitrogen: r.nonUreaNitrogen,
      phosphorus: r.phosphorus,
      potassium: r.potassium,
      sulfur: r.sulfur
    },
    limeFraction: r.limeFraction * 100, // decimal -> percentage
    fractionAnnualBurntCrop: r.fractionAnnualBurntCrop * 100, // decimal -> percentage
  }))
}
export function transformProducerGrainsPostV3_3FormToRecord(form: ProducerGrainsPostV3_3FormT): ProducerGrainsPostV3_3RecordT {
  return form.map(f => ({
    ...f,
    stableId: f.stableId ?? uuidv4(), // generate a stableId if a new grain record
    nonUreaNitrogen: f.activeIngredients.nonUreaNitrogen,
    phosphorus: f.activeIngredients.phosphorus,
    potassium: f.activeIngredients.potassium,
    sulfur: f.activeIngredients.sulfur,
    limeFraction: f.limeFraction / 100, // percentage - >decimal
    fractionAnnualBurntCrop: f.fractionAnnualBurntCrop / 100, // percentage - >decimal
  }))
}

export const GrainAllocationRecordTB = Type.Object({
  grainRecordStableId: Type.String(),
  allocation: NumberMinZeroDefaultUndefinedTB({ maximum: 1 })
})
export type GrainAllocationRecordT = Static<typeof GrainAllocationRecordTB>

export const GrainAllocationFormTB = Type.Composite([
  Type.Omit(GrainAllocationRecordTB, ['allocation']),
  Type.Object({
    allocation: NumberMinZeroDefaultUndefinedTB({ maximum: 100 })
  })
])
export type GrainAllocationFormT = Static<typeof GrainAllocationFormTB>

export const ConsumablesAllocationRecordTB = Type.Object({
  cattle: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 1 })),
  sheep: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 1 })),
  grain: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 1 })),
  wool: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 1 })),
  grainAllocations: Type.Optional(Type.Array(GrainAllocationRecordTB)),
})
export type ConsumablesAllocationRecordT = Static<typeof ConsumablesAllocationRecordTB>

export const ConsumablesAllocationFormTB = Type.Object({
  cattle: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 100 })),
  sheep: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 100 })),
  grain: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 100 })),
  wool: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 100 })),
  // grainAllocations: Type.Optional(Type.Array(GrainAllocationFormTB)),
})
export type ConsumablesAllocationFormT = Static<typeof ConsumablesAllocationFormTB>

// TODO: should "record" numbers default to undefined? should be either a number, or field is undefined?
export const ProductionFarmRecordTB = Type.Object({
  cattleLiveweightSoldKgs: Type.Optional(NumberMinZeroDefaultUndefinedTB()),
  sheepLiveweightSoldKgs: Type.Optional(NumberMinZeroDefaultUndefinedTB()),
  cleanWoolYieldPercentage: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 1 })),
  greasyWoolKgs: Type.Optional(NumberMinZeroDefaultUndefinedTB()),
  consumablesAllocation: Type.Object({
    petrolDiesel: ConsumablesAllocationRecordTB,
    electricity: ConsumablesAllocationRecordTB
  })
})
export type ProductionFarmRecordT = Static<typeof ProductionFarmRecordTB>

export function generateDynamicConsumablesAllocationFormTB (property: PropertyT, report: ReportWithPartialInputsT) {
  const requiredComponents:any = {}
  if (property.cattleFarm) {
    requiredComponents['cattle'] = NumberMinZeroDefaultUndefinedTB({maximum: 100})
  }
  if (property.sheepFarm) {
    requiredComponents['sheep'] = NumberMinZeroDefaultUndefinedTB({maximum: 100})
    if ('productionInformation' in report && report.productionInformation?.consumablesAllocation.electricity.wool !== undefined) {
      requiredComponents['wool'] = NumberMinZeroDefaultUndefinedTB({maximum: 100})
    }
  }
  if (property.grainFarm && report.algorithmVersion < 3.1) {
    requiredComponents['grain'] = NumberMinZeroDefaultUndefinedTB({maximum: 100})

  } else if (property.grainFarm && report.algorithmVersion >= 3.1) {
    const grainRecords = report.grainInformationPostV3_3 || report.grainInformation
    if (grainRecords && grainRecords.length > 0) {
      grainRecords.forEach((r) => {
        requiredComponents[`grainAllocation_${r.stableId}`] = NumberMinZeroDefaultUndefinedTB({maximum: 100})
      })
    }
  }
  return Type.Object(requiredComponents)
}

export function generateDynamicProductionFormTB (property: PropertyT, report: ReportWithPartialInputsT) {
  const requiredComponents:any = {
    petrolDieselAllocation: generateDynamicConsumablesAllocationFormTB(property, report),
    electricityAllocation: generateDynamicConsumablesAllocationFormTB(property, report)
  }
  if (property.cattleFarm) {
    requiredComponents['cattleLiveweightSoldKgs'] = NumberMinZeroDefaultUndefinedTB()
  }
  if (property.sheepFarm) {
    requiredComponents['sheepLiveweightSoldKgs'] = NumberMinZeroDefaultUndefinedTB()
    if (report.algorithmVersion < 3) {
      requiredComponents['cleanWoolYieldPercentage'] = NumberMinZeroDefaultUndefinedTB({maximum: 100})
      requiredComponents['greasyWoolKgs'] = NumberMinZeroDefaultUndefinedTB()
    }
  }
  return Type.Object(requiredComponents)
}

export const ProductionFarmFormTB = Type.Composite([
  Type.Omit(ProductionFarmRecordTB, ['consumablesAllocation', 'cleanWoolYieldPercentage']),
  Type.Object({
    cleanWoolYieldPercentage: Type.Optional(NumberMinZeroDefaultUndefinedTB({ maximum: 100 })),
    petrolDieselAllocation: ConsumablesAllocationFormTB,
    electricityAllocation: ConsumablesAllocationFormTB
  })
])
export type ProductionFarmFormT = Static<typeof ProductionFarmFormTB>

function convertGrainAllocationsFromArray(grainAllocations: GrainAllocationRecordT[], cropRecords: ProducerGrainsPostV3_3RecordT | ProducerGrainsPreV3_4RecordT | undefined): Record<string, number | undefined> {
  return Object.fromEntries(
    (cropRecords ?? []).map((crop) => {
      const matchAllocation = (grainAllocations ?? []).find((a) => a.grainRecordStableId === crop.stableId)
      return [
        `grainAllocation_${crop.stableId}`,
        matchAllocation ? matchAllocation.allocation * 100 : undefined
      ]
    })
  )
}

// transform functions from record -> form and form -> record
export function transformProductionFarmRecordToForm(record: Partial<ProductionFarmRecordT>, cropRecords: ProducerGrainsPostV3_3RecordT | ProducerGrainsPreV3_4RecordT | undefined): ProductionFarmFormT {
  return {
    ...omit(record, ['consumablesAllocation']),
    cleanWoolYieldPercentage: record.cleanWoolYieldPercentage ? record.cleanWoolYieldPercentage * 100 : undefined, // decimal -> percentage
    petrolDieselAllocation: {
      cattle: record.consumablesAllocation?.petrolDiesel.cattle !== undefined ? record.consumablesAllocation.petrolDiesel.cattle * 100 : undefined, // decimal -> percentage
      sheep: record.consumablesAllocation?.petrolDiesel.sheep !== undefined ? record.consumablesAllocation.petrolDiesel.sheep * 100 : undefined, // decimal -> percentage
      grain: record.consumablesAllocation?.petrolDiesel.grain !== undefined ? record.consumablesAllocation.petrolDiesel.grain * 100 : undefined, // decimal -> percentage
      wool: record.consumablesAllocation?.petrolDiesel.wool !== undefined ? record.consumablesAllocation.petrolDiesel.wool * 100 : undefined, // decimal -> percentage
      ...(record.consumablesAllocation?.petrolDiesel.grainAllocations
        ? convertGrainAllocationsFromArray(record.consumablesAllocation?.petrolDiesel.grainAllocations, cropRecords)
        : undefined
      )
    },
    electricityAllocation: {
      cattle: record.consumablesAllocation?.electricity.cattle !== undefined ? record.consumablesAllocation.electricity.cattle * 100 : undefined, // decimal -> percentage
      sheep: record.consumablesAllocation?.electricity.sheep !== undefined ? record.consumablesAllocation.electricity.sheep * 100 : undefined, // decimal -> percentage
      grain: record.consumablesAllocation?.electricity.grain !== undefined ? record.consumablesAllocation.electricity.grain * 100 : undefined, // decimal -> percentage
      wool: record.consumablesAllocation?.electricity.wool !== undefined ? record.consumablesAllocation.electricity.wool * 100 : undefined, // decimal -> percentage
      ...(record.consumablesAllocation?.electricity.grainAllocations
        ? convertGrainAllocationsFromArray(record.consumablesAllocation.electricity.grainAllocations, cropRecords)
        : undefined
      )
    }
  };
}

function convertGrainAllocationsToArray(consumablesAllocation: ConsumablesAllocationFormT): GrainAllocationRecordT[] | undefined {
  const out: GrainAllocationRecordT[] = []
  Object.keys(consumablesAllocation).filter(k => k.includes('grainAllocation_')).forEach(k => {
    const record = consumablesAllocation[k as keyof ConsumablesAllocationFormT]
    if (record !== undefined) {
      out.push({
        grainRecordStableId: k.replace('grainAllocation_', ''),
        allocation: record !== 0 ? record / 100 : 0
      })
    }
  })
  return out.length > 0 ? out : undefined
}

export function transformProductionFarmFormToRecord(form: ProductionFarmFormT): ProductionFarmRecordT {
  return {
    ...omit(form, ['petrolDieselAllocation', 'electricityAllocation']),
    cattleLiveweightSoldKgs: form.cattleLiveweightSoldKgs ?? 0,
    sheepLiveweightSoldKgs: form.sheepLiveweightSoldKgs ?? 0,
    greasyWoolKgs: form.greasyWoolKgs ?? 0,
    cleanWoolYieldPercentage: (form.cleanWoolYieldPercentage ?? 0) / 100, // percentage -> decimal
    consumablesAllocation: {
      petrolDiesel: {
        cattle: form.petrolDieselAllocation.cattle !== undefined ? form.petrolDieselAllocation.cattle / 100 : undefined, // percentage -> decimal
        sheep: form.petrolDieselAllocation.sheep !== undefined ? form.petrolDieselAllocation.sheep / 100 : undefined, // percentage -> decimal
        grain: form.petrolDieselAllocation.grain !== undefined ? form.petrolDieselAllocation.grain / 100 : undefined, // percentage -> decimal
        wool: form.petrolDieselAllocation.wool !== undefined ? form.petrolDieselAllocation.wool / 100 : undefined, // percentage -> decimal
        grainAllocations: convertGrainAllocationsToArray(form.petrolDieselAllocation)
      },
      electricity: {
        cattle: form.electricityAllocation.cattle !== undefined ? form.electricityAllocation.cattle / 100 : undefined, // percentage -> decimal
        sheep: form.electricityAllocation.sheep !== undefined ? form.electricityAllocation.sheep / 100 : undefined, // percentage -> decimal
        grain: form.electricityAllocation.grain !== undefined ? form.electricityAllocation.grain / 100 : undefined, // percentage -> decimal
        wool: form.electricityAllocation.wool !== undefined ? form.electricityAllocation.wool / 100 : undefined, // percentage -> decimal
        grainAllocations: convertGrainAllocationsToArray(form.electricityAllocation)
      },
    }
  };
}

// modal
export const FertilisersUsedTB = Type.Array(ProducerFertiliserTB, { minItems: 1 })
export type FertilisersUsedT = Static<typeof FertilisersUsedTB>
